import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BACKEND_API_URL } from "../../config";

// Slice
const initialState = {
  data: {
      _option: '',
      company: '',
      contactName: '',
      email: '',
      contactPhone: '',
      servicesProvided: '',
      agreementFile: null,
    }, 
  status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
  loading: false,
  error: null,
  formErrors: {},
}

export const fetchInitialState = createAsyncThunk(
  'LocalITCompanyDetails/fetch', 
  async (data, { getState, dispatch }) => {
    const {token} = getState().navigator;
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/onboarding/local_it_company?token=${token}`);
        if (!response.ok) {
          throw new Error('Failed to fetch initial state');
        }
        const initialState = await response.json();
        // console.log(initialState);
        return initialState;
      } catch (error) {
        console.error('Error fetching initial state:', error);
        return initialState.data
      }
    
  })


const IT_COMPANY_REQUIRED_FIELDS = [
  'company',
  'contactName',
  'email',
  'contactPhone',
  'servicesProvided',
];

// Create New State
export const SubmitLocalITCompanyDetails = createAsyncThunk(
    'LocalITCompanyDetails/submit',
    async (data, { getState, dispatch, rejectWithValue }) => {
        try {
          const {token, currentStep} = getState().navigator;

          if (data._option === '' ) {
            return rejectWithValue("Please select the option.");
          }

          // The agreement is optional and is saved on its own as soon as it is
          // picked or dropped, so a file upload / delete is a partial save: the
          // rest of the form is allowed to still be empty or invalid at that point.
          const isFileOnlySave = data.agreementFile instanceof File || data.agreementFile === 'delete';

          const { formErrors } = getState().local_it_company;
          const isFormValid = Object.values(formErrors).every((error) => error === '');

          if (!isFormValid && data._option === 'Yes' && !isFileOnlySave ) {
            // Handle validation errors
            return rejectWithValue("Please enter valid data in all the fields");
          }

          if (data._option === 'Yes' && !isFileOnlySave) {
            const isFormFilled = IT_COMPANY_REQUIRED_FIELDS.every(
              (field) => data[field] !== undefined && data[field] !== null && String(data[field]).trim() !== ''
            );
            if (!isFormFilled) {
              return rejectWithValue('Please fill out all the fields');
            }
          }

          // Create a new FormData instance
          const formData = new FormData();
          Object.keys(data).forEach((key) => {
              if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
                  // Stringify object data before appending
                  formData.append(key, JSON.stringify(data[key]));
              } else {
                  formData.append(key, data[key]);
              }
          });

          // Add currentStep to the FormData. The backend advances the saved step by
          // one whenever current_step is present, so a file-only save must omit it —
          // otherwise uploading or deleting the agreement would move the user on.
          if (!isFileOnlySave) {
            formData.append('current_step', currentStep);
          }

          const response = await fetch(`${BACKEND_API_URL}/api/onboarding/local_it_company?token=${token}`, {
              method: 'POST',
              body: formData,
          });

          const response_data =  await response.json();
          // A failed save must reject — returning here would resolve the thunk,
          // replace the form data with undefined and move the user to the next step.
          if (!response.ok) {
            return rejectWithValue(response_data?.error || 'Could not save the Local IT Company details. Please try again.');
          }
          // Handle the response data
          // // console.log('Form submitted successfully', response_data);

          return response_data.data;
        } catch (error) {
          // Handle submission errors
          console.error('Form submission error', error);
          // Returning the error would count as a success, so it is rejected instead
          return rejectWithValue('Could not save the Local IT Company details. Please check your connection and try again.');
        }
      }
)




export const localITCompanyDetailsSlice = createSlice({
    name: 'LocalITCompanyDetails',
    initialState,
    reducers: {
      updateLocalITCompanyDetails: (state, action) => {
        const { field, value } = action.payload;
        return { ...state, data: {...state.data, [field]: value }}
      },
      updateLocalITCompanyOption: (state, action) => {
        state.data._option = action.payload;
      },
      setFieldError: (state, action) => {
        const { field, error } = action.payload;
        state.formErrors[field] = error;
      },
    },
    extraReducers: (builder) => {
        builder
          // FETCH
          .addCase(fetchInitialState.pending, (state) => {
            state.loading = true;
          })
          .addCase(fetchInitialState.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
          })
          .addCase(fetchInitialState.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })

          // CREATE or UPDATE
          .addCase(SubmitLocalITCompanyDetails.pending, (state) => {
            state.loading = true;
          })
          .addCase(SubmitLocalITCompanyDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
          })
          .addCase(SubmitLocalITCompanyDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })
      },
  });


export const { updateLocalITCompanyDetails, updateLocalITCompanyOption, setFieldError } = localITCompanyDetailsSlice.actions;

export default localITCompanyDetailsSlice.reducer;
  